import React, { useState, useEffect, useCallback } from 'react'
import {
  Card,
  CardBody,
  Input,
  Select,
  SelectItem,
  Switch,
  Chip,
} from '@heroui/react'
import { Button } from '@trafiflow/shared-ui'
import { Plus, Trash2, AlertCircle } from 'lucide-react'
import { funnelApi } from '../../api/funnel-api'
import type { OfferSet, OfferSetItem, CreateOfferSetPayload } from '../../types'

// ---------------------------------------------------------------------------
// Hook: useOfferSetEditor — state management + API calls
// ---------------------------------------------------------------------------

interface OfferSetFormState {
  name: string
  description: string
  selectionMode: 'random' | 'weighted'
  isActive: boolean
  items: OfferSetItem[]
}

function useOfferSetEditor(offerSetId?: string) {
  const [existingSets, setExistingSets] = useState<OfferSet[]>([])
  const [selectedSetId, setSelectedSetId] = useState<string | undefined>(offerSetId)
  const [isCreatingNew, setIsCreatingNew] = useState(!offerSetId)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState<OfferSetFormState>({
    name: '',
    description: '',
    selectionMode: 'random',
    isActive: true,
    items: [],
  })

  const updateForm = useCallback(<K extends keyof OfferSetFormState>(key: K, value: OfferSetFormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }, [])

  const resetForm = useCallback(() => {
    setForm({ name: '', description: '', selectionMode: 'random', isActive: true, items: [] })
  }, [])

  const loadOfferSetDetails = useCallback(async (id: string) => {
    try {
      setLoading(true)
      const response = await funnelApi.getOfferSet(id)
      const set = response.data
      setForm({
        name: set.name,
        description: set.description || '',
        selectionMode: set.selectionMode,
        isActive: set.isActive,
        items: set.items || [],
      })
    } catch (err) {
      setError('Failed to load offer set details')
      console.error('Error loading offer set:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const response = await funnelApi.listOfferSets()
        setExistingSets(response.data)
      } catch (err) {
        setError('Failed to load offer sets')
        console.error('Error loading offer sets:', err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (selectedSetId && !isCreatingNew) {
      loadOfferSetDetails(selectedSetId)
    }
  }, [selectedSetId, isCreatingNew, loadOfferSetDetails])

  const createSet = useCallback(async (): Promise<string | null> => {
    if (!form.name.trim()) {
      setError('Name is required')
      return null
    }
    try {
      setLoading(true)
      setError(null)
      const payload: CreateOfferSetPayload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        selectionMode: form.selectionMode,
        isActive: form.isActive,
      }
      const response = await funnelApi.createOfferSet(payload)
      const newSetId = response.data.id
      if (form.items.length > 0) {
        await Promise.all(
          form.items.map((item, i) =>
            funnelApi.addOfferToSet(newSetId, { offerId: item.offerId, weight: item.weight, position: i }),
          ),
        )
      }
      return newSetId
    } catch (err) {
      setError('Failed to create offer set')
      console.error('Error creating offer set:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [form])

  const updateSet = useCallback(async (): Promise<boolean> => {
    if (!selectedSetId || !form.name.trim()) {
      setError('Name is required')
      return false
    }
    try {
      setLoading(true)
      setError(null)
      await funnelApi.updateOfferSet(selectedSetId, {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        selectionMode: form.selectionMode,
        isActive: form.isActive,
      })
      return true
    } catch (err) {
      setError('Failed to update offer set')
      console.error('Error updating offer set:', err)
      return false
    } finally {
      setLoading(false)
    }
  }, [selectedSetId, form])

  const addOffer = useCallback(async (offerId: string, weight: number) => {
    if (selectedSetId && !isCreatingNew) {
      try {
        setLoading(true)
        await funnelApi.addOfferToSet(selectedSetId, { offerId, weight, position: form.items.length })
        await loadOfferSetDetails(selectedSetId)
      } catch (err) {
        setError('Failed to add offer to set')
        console.error('Error adding offer:', err)
      } finally {
        setLoading(false)
      }
    } else {
      const newItem: OfferSetItem = {
        id: `temp-${Date.now()}`,
        offerSetId: selectedSetId || 'new',
        offerId,
        weight: form.selectionMode === 'weighted' ? weight : 0,
        position: form.items.length,
        createdAt: new Date().toISOString(),
      }
      updateForm('items', [...form.items, newItem])
    }
  }, [selectedSetId, isCreatingNew, form.items, form.selectionMode, loadOfferSetDetails, updateForm])

  const removeOffer = useCallback(async (offerId: string) => {
    if (selectedSetId && !isCreatingNew) {
      try {
        setLoading(true)
        await funnelApi.removeOfferFromSet(selectedSetId, offerId)
        await loadOfferSetDetails(selectedSetId)
      } catch (err) {
        setError('Failed to remove offer from set')
        console.error('Error removing offer:', err)
      } finally {
        setLoading(false)
      }
    } else {
      updateForm('items', form.items.filter((item) => item.offerId !== offerId))
    }
  }, [selectedSetId, isCreatingNew, form.items, loadOfferSetDetails, updateForm])

  const totalWeight = form.items.reduce((sum, item) => sum + item.weight, 0)
  const isWeightValid = form.selectionMode === 'random' || totalWeight === 100

  return {
    existingSets, selectedSetId, setSelectedSetId,
    isCreatingNew, setIsCreatingNew,
    loading, error, form, updateForm, resetForm,
    createSet, updateSet, addOffer, removeOffer,
    totalWeight, isWeightValid,
  }
}

// ---------------------------------------------------------------------------
// Sub-component: OfferItemRow
// ---------------------------------------------------------------------------

interface OfferItemRowProps {
  item: OfferSetItem
  showWeight: boolean
  loading: boolean
  onRemove: (offerId: string) => void
}

const OfferItemRow = React.memo(function OfferItemRow({ item, showWeight, loading, onRemove }: OfferItemRowProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-tf-bg-muted rounded-lg">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium">{item.offerId}</span>
        {showWeight && (
          <Chip size="sm" variant="flat">{item.weight}%</Chip>
        )}
      </div>
      <button onClick={() => onRemove(item.offerId)} className="text-red-600 hover:text-red-700" disabled={loading}>
        <Trash2 size={16} />
      </button>
    </div>
  )
})

// ---------------------------------------------------------------------------
// Sub-component: AddOfferForm
// ---------------------------------------------------------------------------

interface AddOfferFormProps {
  showWeight: boolean
  loading: boolean
  onAdd: (offerId: string, weight: number) => void
}

function AddOfferForm({ showWeight, loading, onAdd }: AddOfferFormProps) {
  const [offerId, setOfferId] = useState('')
  const [weight, setWeight] = useState(50)

  const handleAdd = () => {
    if (!offerId.trim()) return
    onAdd(offerId.trim(), weight)
    setOfferId('')
    setWeight(50)
  }

  return (
    <div className="flex gap-2">
      <Input placeholder="Offer ID" value={offerId} onValueChange={setOfferId} variant="bordered" size="sm" className="flex-1" />
      {showWeight && (
        <Input
          type="number" placeholder="Weight" value={String(weight)}
          onValueChange={(val) => setWeight(parseInt(val, 10) || 0)}
          variant="bordered" size="sm" className="w-24"
          endContent={<span className="text-xs text-tf-text-muted">%</span>}
        />
      )}
      <Button variant="secondary" size="sm" onClick={handleAdd} disabled={!offerId.trim() || loading}>
        <Plus size={16} /> Add
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main: OfferSetEditor
// ---------------------------------------------------------------------------

interface OfferSetEditorProps {
  offerSetId?: string
  onSelect: (setId: string) => void
  onCancel: () => void
}

export function OfferSetEditor({ offerSetId, onSelect, onCancel }: OfferSetEditorProps) {
  const {
    existingSets, selectedSetId, setSelectedSetId,
    isCreatingNew, setIsCreatingNew,
    loading, error, form, updateForm, resetForm,
    createSet, updateSet, addOffer, removeOffer,
    totalWeight, isWeightValid,
  } = useOfferSetEditor(offerSetId)

  const handleSave = async () => {
    if (isCreatingNew) {
      const newId = await createSet()
      if (newId) onSelect(newId)
    } else {
      const ok = await updateSet()
      if (ok && selectedSetId) onSelect(selectedSetId)
    }
  }

  return (
    <Card className="w-full">
      <CardBody className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 p-3 bg-tf-feedback-error-bg text-tf-feedback-error-text rounded-lg">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!isCreatingNew && existingSets.length > 0 && (
          <div className="space-y-2">
            <Select
              label="Select Existing Offer Set" placeholder="Choose an offer set"
              selectedKeys={selectedSetId ? [selectedSetId] : []}
              onSelectionChange={(keys) => setSelectedSetId(Array.from(keys)[0] as string)}
              variant="bordered"
            >
              {existingSets.map((set) => (
                <SelectItem key={set.id}>{set.name} ({set.selectionMode})</SelectItem>
              ))}
            </Select>
            <Button variant="secondary" size="sm" onClick={() => { setIsCreatingNew(true); setSelectedSetId(undefined); resetForm() }}>
              Or Create New Set
            </Button>
          </div>
        )}

        {(isCreatingNew || selectedSetId) && (
          <>
            <Input label="Set Name" labelPlacement="outside" placeholder="Enter offer set name" value={form.name} onValueChange={(v) => updateForm('name', v)} isRequired variant="bordered" maxLength={100} />
            <Input label="Description (optional)" labelPlacement="outside" placeholder="Enter description" value={form.description} onValueChange={(v) => updateForm('description', v)} variant="bordered" maxLength={500} />

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Selection Mode</label>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${form.selectionMode === 'random' ? 'font-semibold' : 'text-tf-text-muted'}`}>Random</span>
                <Switch size="sm" isSelected={form.selectionMode === 'weighted'} onValueChange={(c) => updateForm('selectionMode', c ? 'weighted' : 'random')} />
                <span className={`text-sm ${form.selectionMode === 'weighted' ? 'font-semibold' : 'text-tf-text-muted'}`}>Weighted</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Active</label>
              <Switch size="sm" isSelected={form.isActive} onValueChange={(v) => updateForm('isActive', v)} />
            </div>

            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold mb-3">Offers in Set</h4>
              {form.items.length === 0 && <p className="text-sm text-tf-text-muted mb-3">No offers added yet</p>}
              {form.items.length > 0 && (
                <div className="space-y-2 mb-4">
                  {form.items.map((item) => (
                    <OfferItemRow key={item.offerId} item={item} showWeight={form.selectionMode === 'weighted'} loading={loading} onRemove={removeOffer} />
                  ))}
                </div>
              )}
              {form.selectionMode === 'weighted' && form.items.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className={isWeightValid ? 'text-green-600' : 'text-red-600'}>Total Weight: {totalWeight}%</span>
                    {!isWeightValid && <span className="text-red-600 text-xs">Must equal 100%</span>}
                  </div>
                </div>
              )}
              <AddOfferForm showWeight={form.selectionMode === 'weighted'} loading={loading} onAdd={addOffer} />
            </div>

            <div className="flex gap-2 pt-4 border-t">
              <Button variant="secondary" onClick={onCancel} disabled={loading} className="flex-1">Cancel</Button>
              <Button variant="primary" onClick={handleSave} disabled={!form.name.trim() || !isWeightValid || loading} className="flex-1">
                {loading ? 'Saving...' : isCreatingNew ? 'Create & Select' : 'Save & Select'}
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  )
}
